import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  Users,
  Calendar,
  ChevronDown,
  ChevronRight,
  X,
  IndianRupee,
  BadgeDollarSign,
  ShieldCheck,
  Settings2,
  UserCog,
  UserPlus,
} from "lucide-react";


interface SidebarProps {
  isOpen: boolean;
  isMobile: boolean;
  onClose: () => void;
  userRole?: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  isMobile,
  onClose,
  userRole,
}) => {
  const [expandedItem, setExpandedItem] = useState<string | null>("Schedules");

  const handleItemClick = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}
      <aside
        className={`fixed left-0 top-0 h-full bg-white mt-16 overflow-y-auto shadow-lg z-50 transition-all duration-300   
          ${isMobile ? "w-[280px]" : "w-64 top-16"}`}
      >
        {isMobile && (
          <div className="h-14 bg-[#0071bd] flex items-center justify-between px-4">
            <span className="text-white font-semibold">Menu</span>
            <button
              onClick={onClose}
              className="text-white p-2 hover:bg-[#0062a3] rounded-lg"
            >
              <X className="w-5 h-5 mr-1 text-[#0071bd]" />
            </button>
          </div>
        )}
        <nav className="p-4">
          <div>
            {/* Admin Actions Section - Only for SUPERADMIN, styled as top admin actions for all */}

            {/* Candidates Section - Enhanced style with icons */}
            {userRole !== "TEMPORARY" &&
              userRole !== "VENDOR" &&
              userRole !== "BETTING" &&
              userRole !== "BGVTEAM" &&
               userRole !== "TEMPADMIN"  &&
              userRole !== "CANDIDATE" && (
                <button
                  onClick={() =>
                    setExpandedItem(
                      expandedItem === "Candidates" ? null : "Candidates"
                    )
                  }
                  className="flex items-center w-full p-3 mt-4 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  <Users className="w-5 h-5 mr-1 text-[#0071bd]" />
                  <span className="flex-1 text-left flex items-center gap-2">Candidates</span>
                  {expandedItem === "Candidates" ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
              )}
            {expandedItem === "Candidates" && userRole !== "TEMPORARY" && (
              <div className="ml-8 mt-2 space-y-2">
                {["All", "Selected", "Offered"].map((label) => (
                  <NavLink
                    key={label}
                    to={`/candidates/${label.toLowerCase().replace(" ", "-")}`}
                    onClick={handleItemClick}
                    className={({ isActive }) =>
                      `block p-2 rounded-lg flex items-center gap-2 ${isActive
                        ? "bg-blue-50 text-blue-600 font-bold"
                        : "text-gray-700 hover:bg-gray-100"
                      }`
                    }
                  >
                    <Users className="w-4 h-4 mr-1 text-[#0071bd]" />
                    {label}
                  </NavLink>
                ))}
                {userRole === "SUPERADMIN" && (
                  <NavLink
                    key="Paid"
                    to={`/candidates/paid`}
                    onClick={handleItemClick}
                    className={({ isActive }) =>
                      `block p-2 rounded-lg flex items-center gap-2 ${isActive
                        ? "bg-blue-50 text-blue-600 font-bold"
                        : "text-gray-700 hover:bg-gray-100"
                      }`
                    }
                  >
                    <BadgeDollarSign className="w-4 h-4 mr-1 text-[#0071bd]" />
                    Paid
                  </NavLink>
                )}

                {/* Interviews Dashboard link */}
                {(userRole === "SUPERADMIN" || userRole === "ADMIN") &&(
                    <NavLink
                  key="InterviewsDashboard"
                  to="/candidates/interviews-dashboard"
                  onClick={handleItemClick}
                  className={({ isActive }) =>
                    `block p-2 rounded-lg flex items-center gap-2 ${isActive
                      ? "bg-blue-50 text-blue-600 font-bold"
                      : "text-gray-700 hover:bg-gray-100"
                    }`
                  }
                >
                  <Calendar className="w-4 h-4 mr-1 text-[#0071bd]" />
                  Dashboard
                </NavLink>
                )}
              
                {/* File Storage submenu */}
                <NavLink
                  key="OfferLetters"
                  to="/candidates/offer-letters"
                  onClick={handleItemClick}
                  className={({ isActive }) =>
                    `block p-2 rounded-lg flex items-center gap-2 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#0071bd] shadow-md group relative overflow-hidden
                    ${isActive
                      ? "bg-[#0071bd]/10 text-[#0071bd] font-bold shadow-lg"
                      : "text-gray-700 hover:bg-[#0071bd]/20 hover:text-[#0071bd]"
                    }`
                  }
                  aria-label="Offer Letters"
                  tabIndex={0}
                  style={{
                    boxShadow: '0 2px 8px 0 rgba(0,113,189,0.08)',
                    transition: 'background 0.3s, color 0.3s, box-shadow 0.3s',
                  }}
                >
                               
                  <span className="relative flex items-center justify-center w-6 h-6">
                    {/* PDF Icon SVG */}
                    <svg className="w-5 h-5 text-[#e53e3e] group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="3" width="18" height="18" rx="2" fill="#fff" stroke="#e53e3e" strokeWidth="1.5" />
                      <path d="M7 8h10M7 12h10M7 16h6" stroke="#e53e3e" strokeWidth="1.5" strokeLinecap="round" />
                      <text x="12" y="19" textAnchor="middle" fontSize="7" fill="#e53e3e" fontWeight="bold" fontFamily="Arial">PDF</text>
                    </svg>
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#0a3d2c] rounded-full animate-pulse shadow-lg"></span>
                  </span>

                  <span className="truncate font-semibold tracking-wide group-hover:tracking-widest transition-all duration-300">Offer Letters</span>
                  <span className="absolute left-4 bottom-1 w-0 h-0.5 bg-[#0071bd] group-hover:w-3/4 transition-all duration-300"></span>

                </NavLink>
                <NavLink
                  key="FileStorage"
                  to="/candidates/filestorage"
                  onClick={handleItemClick}
                  className={({ isActive }) =>
                    `block p-2 rounded-lg flex items-center gap-2 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#0071bd] shadow-md group relative overflow-hidden
                    ${isActive
                      ? "bg-[#0071bd]/10 text-[#0071bd] font-bold shadow-lg"
                      : "text-gray-700 hover:bg-[#0071bd]/20 hover:text-[#0071bd]"
                    }`
                  }
                  aria-label="File Manager"
                  tabIndex={0}
                  style={{
                    boxShadow: '0 2px 8px 0 rgba(0,113,189,0.08)',
                    transition: 'background 0.3s, color 0.3s, box-shadow 0.3s',
                  }}
                >

                  <span className="relative flex items-center justify-center w-6 h-6">
                    <svg className="w-5 h-5 text-[#0071bd] group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300" fill="none" viewBox="0 0 24 24">
                      <defs>
                        <linearGradient id="fileIconGradient" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#0071bd" />
                          <stop offset="1" stopColor="#0a3d2c" />
                        </linearGradient>
                      </defs>
                      <path stroke="url(#fileIconGradient)" strokeWidth="1.5" d="M3.75 6.75A2.25 2.25 0 0 1 6 4.5h3.382a2.25 2.25 0 0 1 1.59.659l1.829 1.828a2.25 2.25 0 0 0 1.59.659H18a2.25 2.25 0 0 1 2.25 2.25v7.5A2.25 2.25 0 0 1 18 19.5H6A2.25 2.25 0 0 1 3.75 17.25v-10.5Z" />
                    </svg>
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#0a3d2c] rounded-full animate-pulse shadow-lg"></span>
                  </span>
                  <span className="truncate font-semibold tracking-wide group-hover:tracking-widest transition-all duration-300">File Manager</span>
                  <span className="absolute left-4 bottom-1 w-0 h-0.5 bg-[#0071bd] group-hover:w-3/4 transition-all duration-300"></span>
                </NavLink>

              </div>
            )}
          </div>
          {/* Schedules Section */}
          {userRole !== "BGVTEAM" &&
            userRole !== "VENDOR" && userRole !== "BETTING" &&
            <>
              <button
                onClick={() =>
                  setExpandedItem(
                    expandedItem === "Schedules" ? null : "Schedules"
                  )
                }
                className="flex items-center w-full p-3 mt-4 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <Calendar className="w-5 h-5 mr-1 text-[#0071bd]" />
                <span className="flex-1 text-left">Schedules</span>
                {expandedItem === "Schedules" ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>

              {expandedItem === "Schedules" && (
                <div className="ml-8 mt-2 space-y-2">
                  {(userRole === "TEMPORARY"
                    ? ["Scheduled"]
                    : userRole === "TEMPADMIN"
                      ? ["Scheduled", "Completed"]
                      : userRole === "VENDOR" || userRole == "BETTING"
                        ? []
                        : ["Scheduled", "Completed", "HRContacts"]
                  ).map((label) => (
                    <NavLink
                      key={label}
                      to={`/schedules/${label.toLowerCase()}`}
                      onClick={handleItemClick}
                      className={({ isActive }) =>
                        `block p-2 rounded-lg ${isActive
                          ? "bg-blue-50 text-blue-600"
                          : "text-gray-600 hover:bg-gray-100"
                        }`
                      }
                    >
                      {label}
                    </NavLink>
                  ))}
                  {userRole === "SUPERADMIN" && (
                    <>
                      <NavLink
                        key={"pay"}
                        to={`/schedules/pay`}
                        onClick={handleItemClick}
                        className={({ isActive }) =>
                          `block p-2 rounded-lg ${isActive
                            ? "bg-blue-50 text-blue-600"
                            : "text-gray-600 hover:bg-gray-100"
                          }`
                        }
                      >
                        {"pay"}
                      </NavLink>
                      <NavLink
                        key={"relieving"}
                        to={`/schedules/relieving`}
                        onClick={handleItemClick}
                        className={({ isActive }) =>
                          `block p-2 rounded-lg ${isActive
                            ? "bg-blue-50 text-blue-600"
                            : "text-gray-600 hover:bg-gray-100"
                          }`
                        }
                      >
                        {"relieving"}
                      </NavLink>
                      <NavLink
                        key={"experience"}
                        to={`/schedules/experience`}
                        onClick={handleItemClick}
                        className={({ isActive }) =>
                          `block p-2 rounded-lg ${isActive
                            ? "bg-blue-50 text-blue-600"
                            : "text-gray-600 hover:bg-gray-100"
                          }`
                        }
                      >
                        {"experience"}
                      </NavLink>
                    </>
                  )}
                </div>
              )}
            </>
          }




          {/* Profiles Section */}
          <div>
            {(userRole === "ADMIN" || userRole === "SUPERADMIN") && (
              <button
                onClick={() =>
                  setExpandedItem(
                    expandedItem === "Profiles" ? null : "Profiles"
                  )
                }
                className="flex items-center w-full p-3 mt-4 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <Users className="w-5 h-5 mr-1 text-[#0071bd]" />
                <span className="flex-1 text-left">Profiles</span>
                {expandedItem === "Profiles" ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
            )}

            {expandedItem === "Profiles" && (userRole === "ADMIN" || userRole === "SUPERADMIN") && (
              <div className="ml-8 mt-2 space-y-2">
                {[
                  "All Stored Freshers",
                  "All Stored Experience",
                  "Scheduled Profiles",
                ].map((item) => (
                  <NavLink
                    key={item}
                    to={`/profiles/${item.toLowerCase().replace(/ /g, "")}`}
                    onClick={handleItemClick}
                    className={({ isActive }) =>
                      `block p-2 rounded-lg ${isActive
                        ? "bg-blue-50 text-blue-600"
                        : "text-gray-600 hover:bg-gray-100"
                      }`
                    }
                  >
                    {item}
                  </NavLink>
                ))}
              </div>
            )}

            {/* {(userRole === "SUPERADMIN" || userRole === "BETTING") && (
              <div>
                <button
                  onClick={() =>
                    setExpandedItem(
                      expandedItem === "Betting" ? null : "Betting"
                    )
                  }
                  className="flex items-center w-full p-3 mt-4 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  <BadgeDollarSign className="w-5 h-5 mr-1 text-[#0071bd]" />
                  <span className="flex-1 text-left">Betting</span>
                  {expandedItem === "Betting" ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>

                {expandedItem === "Betting" && (
                  <div className="ml-8 mt-2 space-y-2">
                    <NavLink
                      key={"Co-Efficients"}
                      to={"/betting/coefficients"}
                      onClick={handleItemClick}
                      className={({ isActive }) =>
                        `block p-2 rounded-lg ${isActive
                          ? "bg-blue-50 text-blue-600"
                          : "text-gray-600 hover:bg-gray-100"
                        }`
                      }
                    >
                      {"Co-Efficients"}
                    </NavLink>
                  </div>
                )}
              </div>
            )} */}
          </div>
          {/* BGV Section - new addition */}
          {(userRole === "SUPERADMIN" || userRole === "BGVTEAM") && (
            <div>
              <button
                onClick={() =>
                  setExpandedItem(
                    expandedItem === "BGV" ? null : "BGV"
                  )
                }
                className="flex items-center w-full p-3 mt-4 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <span className="flex-1 text-left flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 mr-1 text-[#0071bd]" />
                  BGV
                </span>
                {expandedItem === "BGV" ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {expandedItem === "BGV" && (
                <div className="ml-8 mt-2 space-y-2">
                  {["In Progress", "Completed", "Paid"].map((label) => (
                    <NavLink
                      key={label}
                      to={`/bgv/${label.toLowerCase().replace(/ /g, "")}`}
                      onClick={handleItemClick}
                      className={({ isActive }) =>
                        `block p-2 rounded-lg ${isActive
                          ? "bg-blue-50 text-blue-600"
                          : "text-gray-600 hover:bg-gray-100"
                        }`
                      }
                    >
                      {label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Payments Section */}
          {(userRole === "SUPERADMIN" || userRole === "VENDOR") && (
            <div>
              <button
                onClick={() =>
                  setExpandedItem(
                    expandedItem === "Payments" ? null : "Payments"
                  )
                }
                className="flex items-center w-full p-3 mt-4 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <IndianRupee className="w-5 h-5 mr-1 text-[#0071bd]" />
                <span className="flex-1 text-left">Payments</span>
                {expandedItem === "Payments" ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {expandedItem === "Payments" && userRole === "VENDOR" && (
                <div className="ml-8 mt-2 space-y-2">
                  <NavLink
                    key="Vendors"
                    to={`/payments/vendors`}
                    onClick={handleItemClick}
                    className={({ isActive }) =>
                      `block p-2 rounded-lg ${isActive
                        ? "bg-blue-50 text-blue-600"
                        : "text-gray-600 hover:bg-gray-100"
                      }`
                    }
                  >
                    Vendors
                  </NavLink>
                </div>
              )}
              {expandedItem === "Payments" && userRole === "SUPERADMIN" && (
                <div className="ml-8 mt-2 space-y-2">
                  {["Vendors", "Accenture"].map((label) => (
                    <NavLink
                      key={label}
                      to={`/payments/${label.toLowerCase()}`}
                      onClick={handleItemClick}
                      className={({ isActive }) =>
                        `block p-2 rounded-lg ${isActive
                          ? "bg-blue-50 text-blue-600"
                          : "text-gray-600 hover:bg-gray-100"
                        }`
                      }
                    >
                      {label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
          {userRole === "SUPERADMIN" && (
            <div>
              <button
                onClick={() =>
                  setExpandedItem(
                    expandedItem === "AdminActions" ? null : "AdminActions"
                  )
                }
                className="flex items-center w-full p-3 mt-4 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <span className="flex-1 text-left flex items-center gap-2">
                  <Settings2 className="w-5 h-5 mr-1 text-[#0071bd]" />
                  Admin Actions
                </span>
                {expandedItem === "AdminActions" ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {expandedItem === "AdminActions" && (
                <div className="ml-8 mt-2 space-y-2">
                  <NavLink
                    key="ManageUsers"
                    to="/admin/manage-access"
                    onClick={handleItemClick}
                    className={({ isActive }) =>
                      `block p-2 rounded-lg flex items-center gap-2 ${isActive
                        ? "bg-blue-50 text-blue-600 font-bold"
                        : "text-gray-700 hover:bg-gray-100"
                      }`
                    }
                  >
                    <UserCog className="w-5 h-5 mr-1 text-[#0071bd]" />
                    Manage Users
                  </NavLink>
                  <NavLink
                    key="CreateUser"
                    to="/admin/create-user"
                    onClick={handleItemClick}
                    className={({ isActive }) =>
                      `block p-2 rounded-lg flex items-center gap-2 ${isActive
                        ? "bg-blue-50 text-blue-600 font-bold"
                        : "text-gray-700 hover:bg-gray-100"
                      }`
                    }
                  >
                    <UserPlus className="w-5 h-5 mr-1 text-[#0071bd]" />
                    Create User
                  </NavLink>
                </div>
              )}
            </div>
          )}
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;  